import api from "./api-utils";

/**
 * Get user display settings for a specific page
 * @param {string} pageName - The page name (e.g., 'OperatingPoints')
 * @returns {Promise<Object>} Response containing UserDisplaySettings array
 * @throws {Error} If the API request fails
 */
export const getUserDisplaySettings = async (pageName) => {
  try {
    const requestBody = { PageName: pageName };
    const response = await api.post(
      "/api/userdisplaysettings/all",
      requestBody
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new user display setting
 * @param {Object} data - The display setting data
 * @param {string} data.PageName - The page name (e.g., 'OperatingPoints')
 * @param {string} data.UserDisplaySettingsName - The name of the display setting
 * @param {string} data.UserDisplaySettingsData - String containing display setting data (format determined by the consuming page)
 * @returns {Promise<Object>} Response containing the created AppUserDisplaySetting
 * @throws {Error} If the API request fails
 */
export const createUserDisplaySetting = async (data) => {
  try {
    const requestBody = {
      PageName: data.PageName,
      UserDisplaySettingsName: data.UserDisplaySettingsName,
      UserDisplaySettingsData: data.UserDisplaySettingsData,
    };

    const response = await api.post(
      "/api/userdisplaysettings/new",
      requestBody
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing user display setting
 * @param {number} userDisplaySettingsId - The ID of the display setting to update
 * @param {Object} data - The display setting data
 * @param {string} data.PageName - The page name (e.g., 'OperatingPoints')
 * @param {string} data.UserDisplaySettingsName - The name of the display setting
 * @param {string} data.UserDisplaySettingsData - String containing display setting data (format determined by the consuming page)
 * @returns {Promise<Object>} Response containing the updated AppUserDisplaySetting
 * @throws {Error} If the API request fails
 */
export const updateUserDisplaySetting = async (userDisplaySettingsId, data) => {
  const url = `/api/userdisplaysettings/${userDisplaySettingsId}`;
  const requestBody = {
    PageName: data.PageName,
    UserDisplaySettingsName: data.UserDisplaySettingsName,
    UserDisplaySettingsData: data.UserDisplaySettingsData,
  };

  try {
    const response = await api.put(url, requestBody);

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a user display setting
 * @param {number} userDisplaySettingsId - The ID of the display setting to delete
 * @returns {Promise<void>}
 * @throws {Error} If the API request fails
 */
export const deleteUserDisplaySetting = async (userDisplaySettingsId) => {
  try {
    await api.delete(`/api/userdisplaysettings/${userDisplaySettingsId}`);
  } catch (error) {
    throw error;
  }
};
